<script lang="ts" setup>
import type { SearchSuggestion, SearchCategory } from '~/types/searchTypes'
import { FACET_CATEGORY_ID } from '~/composables/constants/search'
import buildSearchUrl from '@/utils/filters/buildSearchUrl'

import KeywordLink from './KeywordLink.vue'
import TextWithHighlight from './TextWithHightlight.vue'
import { useAnalyticalDataTracking } from '~/composables/analytics/useAnalyticalDataTracking'
import { useCategoryStore } from '~/store/categoryStore'
import findGenderFromCategoryHierarchy from '~/utils/findGenderFromCategoryHierarchy'

const props = defineProps<{
  searchValue?: string
  suggestions?: SearchSuggestion[]
  categories?: SearchCategory[]
  manufacturers?: string[]
  queryKey: string
}>()

const { t } = useLang()
const categoryStore = useCategoryStore()

const [baseSuggestion, ...remainingSuggestions] = props.suggestions ?? []

const parsedCategories = computed(() =>
  props.categories
    ?.map((category) => ({ ...category, name: getCategoryName(+category.title) }))
    .filter((category) => category.name)
)

function handleOnSuggestionClick(suggestion: SearchSuggestion) {
  useAnalyticalDataTracking().trackMarketplaceSearchBarRefinedSearch({
    oldSearchText: props.searchValue || null,
    newSearchText: suggestion.suggestion,
    newSearchTextCategory: null,
    queryKey: props.queryKey,
  })
}

function handleOnSuggestionCategoryClick(category: SearchCategory) {
  useAnalyticalDataTracking().trackMarketplaceSearchBarRefinedSearch({
    oldSearchText: props.searchValue || null,
    newSearchText: category.title,
    newSearchTextCategory: category.title,
    queryKey: props.queryKey,
  })
}

const getCategoryName = (categoryId: number) => {
  const category = categoryStore.getCategories.find((category) => category.id === categoryId)
  const gender = findGenderFromCategoryHierarchy(category?.hierarchy)

  if (gender != null) {
    return `${category?.name || ''} (${gender})`
  }

  return category?.name
}
</script>

<template>
  <div class="search-suggestions-keywords flex flex-col">
    <KeywordLink
      v-if="baseSuggestion"
      :search-value="props.searchValue"
      :suggestion="baseSuggestion.suggestion"
      :manufacturers="manufacturers"
      @click="handleOnSuggestionClick(baseSuggestion)" />

    <NuxtLink
      v-for="(category, i) in parsedCategories"
      :key="i"
      class="suggestion-category"
      :to="
        buildSearchUrl(searchValue, {
          [FACET_CATEGORY_ID]: category.title,
        })
      "
      @click="handleOnSuggestionCategoryClick(category)">
      <TextWithHighlight :search="props.searchValue" :text="baseSuggestion.suggestion" />
      <span class="text-xs text-gray6 dark:text-dmText3 px-1">
        {{ t('header.in-category') }}
      </span>
      <span class="text-sm font-semibold">{{ category.name }}</span>
    </NuxtLink>

    <div v-if="remainingSuggestions.length" class="flex flex-col pt-4">
      <KeywordLink
        v-for="(suggestion, i) in remainingSuggestions"
        :key="i"
        :search-value="props.searchValue"
        :suggestion="suggestion.suggestion"
        @click="handleOnSuggestionClick(suggestion)" />
    </div>
  </div>
</template>

<style lang="postcss">
.search-suggestions-keywords {
  a {
    @apply text-text1 dark:text-dmText1;

    padding: 0.75rem 1rem;
    text-decoration: none;
    min-width: 15rem;

    &:hover {
      color: initial;
      @apply bg-gray1 dark:bg-dmGray2;
    }
  }
}
</style>
